import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import Image from "next/image";
import { v4 as uuid } from "uuid";
import { FC, useState } from "react";
import { Box, Grid, lighten, useTheme } from "@mui/material";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { SwiperNavigation } from "@/shared/common/layouts/SwiperNavigation";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { createState } from "@/global/store/zustand_store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { PerkParentType } from "./perks.types";

import type { TeamConfig } from "@/global/config/teamConfigs/types";

export const usePerkImageSliderModal = createState({
  isOpen: false,
  perkCode: "" as PerkParentType["perkCode"] | "",
  title: "",
});

interface TSlide {
  component: React.JSX.Element;
}

let swiper: SwiperClass | null = null;

function createSlides(
  teamConfig: TeamConfig,
  perkCode: PerkParentType["perkCode"]
): TSlide[] {
  const slides: { component: JSX.Element }[] = []; // prettier-ignore
  const images = teamConfig?.uiConfigs?.perks?.images;
  if (images && images[perkCode]?.length)
    images[perkCode]?.forEach((itm: string) => {
      slides.push({
        component: (
          <Image
            src={itm}
            alt="suite"
            width={2568}
            height={1445}
            style={{
              width: "100%",
              height: "auto",
            }}
          />
        ),
      });
    });

  return slides as TSlide[];
}

function Thumbnail(props: {
  slide: TSlide;
  isSelected?: boolean;
  onClick?: (s: TSlide) => void;
}) {
  const theme = useTheme();
  const { sm: smUp } = useCssMediaQuery("up");

  return (
    <Box
      onClick={() => props?.onClick?.(props.slide)}
      sx={{
        width: "100%",
        height: smUp ? "48px" : "32px",
        overflow: "hidden",
        borderRadius: 0.5,
        position: "relative",
        transition: "box-shadow 0.2s ease-in-out",
        cursor: props?.isSelected ? "default" : "pointer",
        outline: props.isSelected ? "2px solid" : "1px solid",
        outlineColor: props.isSelected
          ? theme.palette.primary.main
          : theme.palette.divider,
        boxShadow: props.isSelected
          ? `0 0 0 6px ${lighten(theme.palette.primary.main, 0.8)}`
          : "none",
        pointerEvents: props.isSelected ? "none" : "auto",
        "&:hover": {
          boxShadow: !props?.isSelected
            ? `0 0 0 4px ${lighten(theme.palette.primary.main, 0.8)}`
            : `0 0 0 6px ${lighten(theme.palette.primary.main, 0.8)}`,
        },
      }}
    >
      <Image
        src={props.slide.component.props.src}
        alt="suite"
        width={2568}
        height={1445}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  );
}

export const PerkImageSliderModal: FC = () => {
  const teamConfig = useTeamConfig();
  const modalState = usePerkImageSliderModal();
  const [_, setRenderUUID] = useState(uuid());
  const slides = createSlides(teamConfig, modalState.perkCode as any);

  if (!slides.length) return null;

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      hideSecondaryButton
      title={modalState.title}
      isOpen={modalState.isOpen}
      onClose={() => modalState.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderFooter={() => null}
      renderBody={() => (
        <Box mb={2}>
          <Swiper
            spaceBetween={32}
            slidesPerView={1}
            allowTouchMove={false}
            onInit={(instance) => {
              swiper = instance;
              setRenderUUID(uuid());
            }}
            onSlideChangeTransitionEnd={(instance) => {
              swiper = instance;
              setRenderUUID(uuid());
            }}
            loop={true}
          >
            {slides.length > 1 ? (
              <SwiperNavigation.Overlayed
                prevButton={{ offsetLeft: 8 }}
                nextButton={{ offsetRight: 8 }}
              />
            ) : null}

            {slides.map((slide, idx) => (
              <SwiperSlide key={idx}>{slide.component}</SwiperSlide>
            ))}
          </Swiper>

          {slides.length ? (
            <Box mt={2}>
              <Grid container spacing={2} justifyContent={"center"}>
                {slides.map((slide, idx) => (
                  <Grid item key={idx} xs={2.3}>
                    <Thumbnail
                      slide={slide}
                      isSelected={idx === swiper?.realIndex}
                      onClick={() => {
                        swiper?.slideToLoop?.(idx);
                        setRenderUUID(uuid());
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}
        </Box>
      )}
    />
  );
};
